import { useState, type ReactElement } from "react";
import { useParams } from "react-router-dom";
import { usePortal, findStage } from "../state/PortalContext.js";
import { DetailView } from "../renderers/DetailView.js";
import { ActionDialog } from "../renderers/ActionDialog.js";
import type { CatalogAction, CatalogResource } from "../types.js";
import { PageHelp } from "../layout/PageHelp.js";

/** Generic module screen: look up a resource by id, show its detail, run its actions. */
export function ModulePage(): ReactElement {
  const { api, catalog } = usePortal();
  const params = useParams();
  const serviceId = params.service!;
  const stageId = params.stage!;
  const moduleId = params.module!;

  const stage = findStage(catalog, serviceId, stageId);
  const module = stage?.modules.find((m) => m.id === moduleId);
  const resource: CatalogResource | undefined = module?.resources[0];

  const [idInput, setIdInput] = useState("");
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<CatalogAction | null>(null);

  if (!module || !resource) return <p>Select a module.</p>;

  async function load(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const d = (await api.detail(serviceId, stageId, moduleId, resource!.id, id)) as Record<string, unknown>;
      setData(d);
      setLoadedId(id);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setBusy(false);
    }
  }

  const hasDetail = resource.views.some((v) => v.type === "detail");

  return (
    <section aria-labelledby="module-title">
      <h1 id="module-title">{module.displayName ?? module.id}</h1>
      {module.description ? (
        <PageHelp>
          <p>{module.description}</p>
        </PageHelp>
      ) : null}

      {hasDetail ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (idInput) void load(idInput);
          }}
        >
          <label htmlFor="id-input">{resource.displayName ?? resource.id} id</label>
          {resource.lookupHint ? (
            <p id="id-input-hint" className="field-hint">
              {resource.lookupHint}
            </p>
          ) : null}
          <input
            id="id-input"
            value={idInput}
            onChange={(e) => setIdInput(e.target.value)}
            autoComplete="off"
            aria-describedby={resource.lookupHint ? "id-input-hint" : undefined}
          />
          <button type="submit" disabled={!idInput || busy}>
            {busy ? "Loading…" : "Load"}
          </button>
        </form>
      ) : null}

      {error ? (
        <p role="alert" className="field-error">
          {error}
        </p>
      ) : null}

      {data && loadedId ? (
        <>
          <DetailView fields={resource.fields} data={data} />
          {resource.actions.length > 0 ? (
            <>
              <h2>What you can do</h2>
              <ul className="action-list">
                {resource.actions.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className={`danger-${a.danger ?? "low"}`}
                      onClick={() => setDialog(a)}
                      aria-describedby={a.description ? `action-hint-${a.id}` : undefined}
                    >
                      {a.label ?? a.id}
                    </button>
                    {a.description ? (
                      <span id={`action-hint-${a.id}`} className="field-hint">
                        {a.description}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      ) : null}

      {dialog && loadedId ? (
        <ActionDialog
          action={dialog}
          stageId={stageId}
          stageProtected={stage?.protected ?? false}
          onClose={() => setDialog(null)}
          onSubmit={async (body) => {
            await api.action(serviceId, stageId, moduleId, resource.id, loadedId, dialog.id, body);
            await load(loadedId);
          }}
        />
      ) : null}
    </section>
  );
}
